import { appNotOpened, getMe, isAuthLost, me, tenantInactive } from '@yc/api-client'
import { can, usePermissions } from '@yc/ui-kit'

import { router } from './index'

const { load } = usePermissions()

/** 权限属于具体用户：换人登录必须重载，否则会沿用上一个人的权限 */
let permsForUid: number | null = null
let permsUnavailable = false

/**
 * 企业停用/到期（40303）与子系统未开通（40301）不是登录态问题：账号密码本身是好的，
 * 跳登录页只会让用户重新输一遍密码再撞同一堵墙。这类错误要落到各自的文案页。
 */
function unavailableOf(e: unknown): string | null {
  if (tenantInactive(e)) return '40303'
  if (appNotOpened(e)) return '40301'
  return null
}

interface Boot {
  ok: boolean
  /** 非 null 表示是企业停用/子系统未开通，调用方应改去文案页而不是登录页。 */
  blocked: string | null
}

async function bootstrap(): Promise<Boot> {
  if (!getMe()) {
    // 登录态已被后端确认作废（见 session.ts 的 authLost）。
    // refresh 失败会同步触发跳登录页，那次导航又走到这里；没有这个短路就会
    // /me → 401 → refresh → 401 → 再跳转，未登录访问受保护页实测 6 秒 5000+ 次请求。
    if (isAuthLost()) return { ok: false, blocked: null }
    try {
      await me()
    } catch (e) {
      return { ok: false, blocked: unavailableOf(e) }
    }
  }
  const uid = getMe()?.uid ?? null
  if (permsForUid !== uid) {
    try {
      await load('scr')
      permsForUid = uid
      permsUnavailable = false
    } catch (e) {
      const blocked = unavailableOf(e)
      if (blocked) return { ok: false, blocked }
      // 权限取不到时不拦人：真正的权限判定在后端（403），这里只是体验层。
      // 失败（含强制改密期间的 40302）保持 permsForUid 未就位，下次导航自动重试。
      permsUnavailable = true
    }
  }
  return { ok: !!getMe(), blocked: null }
}

function redirectOf(query: Record<string, unknown>): string {
  const q = query.redirect
  return typeof q === 'string' && q.startsWith('/') && !q.startsWith('//') ? q : '/'
}

export function installGuard(): void {
  router.beforeEach(async (to) => {
    // 公开页（/app-unavailable 等）不需要登录态，必须跳过 bootstrap。
    // 不跳过的后果：bootstrap 里的 /me 会 401 → refresh 失败 → setAuthFailureHandler
    // 跳登录页，于是未登录用户永远看不到 /app-unavailable，与「公开页」的语义矛盾。
    if (to.meta.public && to.name !== 'login') return true

    const { ok: loggedIn, blocked } = await bootstrap()

    if (to.meta.public) {
      // login 页：已登录就回首页
      if (loggedIn) return redirectOf(to.query)
      return true
    }

    if (!loggedIn) {
      if (blocked) return { name: 'app-unavailable', query: { code: blocked } }
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    if (getMe()?.must_change_pwd && to.name !== 'force-change-pwd') {
      return { name: 'force-change-pwd', query: { redirect: to.fullPath } }
    }

    const perm = to.meta.perm as string | undefined
    if (perm && !permsUnavailable && !can(perm)) {
      return { name: 'forbidden' }
    }

    return true
  })
}

export function resetPermCache(): void {
  permsForUid = null
  permsUnavailable = false
}
